/**
 * R-8 — qualitative claim audit: extract modal/comparative claims, judge vs SED + citations.
 *
 * Batch / on-demand only — never blocks report dispatch.
 */
import { Type } from '@google/genai';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { DataSource, SiteRagReport } from '../../types/siteRag';
import type { AuditSeverity } from '../../types/userAudit';
import { GEMINI_PRO_MODEL } from '../../config/geminiModels';
import { ai } from '../../services/geminiClient';
import { tracedGeminiCall } from '../telemetry/tracedGeminiCall';
import { logAuditEvent } from '../telemetry/auditTelemetry';
import { extractSections } from './reportTextExtract';
import {
  buildQualitativeClaimJudgePrompt,
  QUALITATIVE_CLAIM_JUDGE_PROMPT_VERSION,
} from './prompts/qualitativeClaimJudge';

export const QUALITATIVE_CLAIM_JUDGE_MODEL = GEMINI_PRO_MODEL;
export const DEFAULT_MAX_CLAIMS = 50;
export const JUDGE_TIMEOUT_MS = 30_000;

export type ClaimVerdict = 'CONFIRMED' | 'UNSUPPORTED' | 'CONTRADICTED';

export interface QualitativeClaim {
  id: string;
  text: string;
  excerpt: string;
  sectionId: string;
  lineNumber: number;
  refIndices: number[];
}

export interface ClaimJudgement {
  verdict: ClaimVerdict;
  rationale: string;
  sedFieldsConsidered: string[];
}

export interface QualitativeClaimEvidence {
  sed: SiteEvidenceDocument | null;
  citedReferences: Array<{ index: number; source: string; field?: string; excerpt: string }>;
  sedExcerpt: string;
}

export interface QualitativeClaimFinding {
  claimId: string;
  claimText: string;
  sectionId: string;
  lineNumber: number;
  refIndices: number[];
  verdict: 'UNSUPPORTED' | 'CONTRADICTED';
  severity: AuditSeverity;
  rationale: string;
  sedFieldsConsidered: string[];
  message: string;
}

export interface QualitativeClaimAuditResult {
  findings: QualitativeClaimFinding[];
  claimsScanned: number;
  claimsJudged: number;
  claimsSkipped: number;
  confirmedCount: number;
  checkedAt: string;
  promptVersion: string;
}

export type QualitativeClaimLlmFn = (prompt: string, ctx: {
  siteId?: string;
  reportId?: string;
  reportType?: string;
}) => Promise<string | null>;

const REF_TOKEN_RE = /\[Ref\s+(\d+)\]/gi;

/** Modal / comparative / qualitative characterization signals. */
const QUALITATIVE_SIGNAL_RE = new RegExp(
  [
    '\\b(favorable|unfavorable|favourable|unfavourable|low[- ]risk|high[- ]risk|straightforward|complex|competitive|',
    'strong|weak|suitable|unsuitable|conducive|challenging|robust|limited|significant|relatively|comparatively|',
    'modest|substantial|promising|concerning|well[- ]suited|poorly[- ]suited|minimal|severe|adequate|insufficient|',
    'favorable|attractive|unattractive|benign|adverse|straightforward|difficult|manageable|unmanageable|',
    'more\\s+\\w+|less\\s+\\w+|better\\s+than|worse\\s+than|higher\\s+than|lower\\s+than|',
    'likely|unlikely|probable|improbable|feasible|infeasible|viable|nonviable|',
    'may|might|could|should|appears|seems|suggests|indicates|supports|undermines)\\b',
  ].join(''),
  'i',
);

/** Predominantly numeric / unit-only sentences (not qualitative). */
const NUMERIC_ONLY_RE = /^[\s\d.,+\-/%°$€£kmMWGHAQIPSDS]+$/i;

function extractRefIndices(text: string): number[] {
  const out = new Set<number>();
  for (const m of text.matchAll(new RegExp(REF_TOKEN_RE.source, 'gi'))) {
    const n = parseInt(m[1]!, 10);
    if (Number.isFinite(n) && n > 0) out.add(n);
  }
  return [...out].sort((a, b) => a - b);
}

function stripCitations(text: string): string {
  return text.replace(/\[Ref\s+[^\]]+\]/gi, '').replace(/\s+/g, ' ').trim();
}

function isNumericOnlyClaim(text: string): boolean {
  const stripped = stripCitations(text)
    .replace(/\b\d+(?:\.\d+)?\s*(?:km|mi|miles|MW|GW|kW|acres|ha|%|°C|°F|mg\/L|ppb|ppm|g\/kWh|USD|\$)\b/gi, '')
    .replace(/\b\d+(?:\.\d+)?\b/g, '')
    .replace(/[,;:()[\]"']/g, ' ')
    .trim();
  if (stripped.length < 8) return true;
  return NUMERIC_ONLY_RE.test(stripped.replace(/\s+/g, ''));
}

function hasQualitativeSignal(text: string): boolean {
  return QUALITATIVE_SIGNAL_RE.test(stripCitations(text));
}

function hashClaimId(sectionId: string, lineNumber: number, text: string): string {
  let h = 0;
  const key = `${sectionId}:${lineNumber}:${text.slice(0, 80)}`;
  for (let i = 0; i < key.length; i++) h = ((h << 5) - h + key.charCodeAt(i)) | 0;
  return `qc-${Math.abs(h).toString(36)}`;
}

function splitSentences(text: string, sectionId: string): Array<{
  text: string;
  sectionId: string;
  lineNumber: number;
}> {
  const lines = text.split('\n');
  const out: Array<{ text: string; sectionId: string; lineNumber: number }> = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!.trim();
    if (line.length < 25) continue;
    const re = /[^.!?\n]+(?:[.!?](?:\s|$)|$)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line)) !== null) {
      const chunk = m[0].trim();
      if (chunk.length >= 25) {
        out.push({ text: chunk, sectionId, lineNumber: i + 1 });
      }
    }
    if (!/[.!?]/.test(line) && line.length >= 40) {
      out.push({ text: line, sectionId, lineNumber: i + 1 });
    }
  }
  return out;
}

/** Extract cited qualitative claims from report narrative sections. */
export function extractQualitativeClaims(report: SiteRagReport): QualitativeClaim[] {
  const sections = extractSections(report);
  const md = (report as SiteRagReport & { markdown?: string }).markdown?.trim() ?? '';
  if (md.length > 100) {
    sections.unshift({ id: 'markdown', content: md });
  }

  const claims: QualitativeClaim[] = [];
  const seen = new Set<string>();

  for (const section of sections) {
    for (const sentence of splitSentences(section.content, section.id)) {
      const refIndices = extractRefIndices(sentence.text);
      if (refIndices.length === 0) continue;
      if (isNumericOnlyClaim(sentence.text)) continue;
      if (!hasQualitativeSignal(sentence.text)) continue;

      const id = hashClaimId(sentence.sectionId, sentence.lineNumber, sentence.text);
      if (seen.has(id)) continue;
      seen.add(id);

      claims.push({
        id,
        text: stripCitations(sentence.text),
        excerpt: sentence.text,
        sectionId: sentence.sectionId,
        lineNumber: sentence.lineNumber,
        refIndices,
      });
    }
  }

  return claims;
}

const FIELD_TO_SED_KEYS: Array<{ pattern: RegExp; keys: string[] }> = [
  { pattern: /grid|interconnect|substation|transmission|power/i, keys: ['grid', 'power'] },
  { pattern: /flood|hazard|seismic|pga|sds|sd1/i, keys: ['hazards', 'seismic', 'flood'] },
  { pattern: /water|cooling|drought|aquifer|wri|stress/i, keys: ['water', 'cooling'] },
  { pattern: /air|aqi|emission|attainment/i, keys: ['air', 'environmental'] },
  { pattern: /wildlife|habitat|species|usfws|ipac/i, keys: ['ecology', 'wildlife'] },
  { pattern: /zoning|land use|parcel|acre/i, keys: ['zoning', 'parcel'] },
  { pattern: /sentiment|stakeholder|community|permit/i, keys: ['sentiment', 'regulatory'] },
];

function pickSedSections(sed: SiteEvidenceDocument, claim: QualitativeClaim, references: DataSource[]): string[] {
  const keys = new Set<string>();
  const haystack = `${claim.text} ${claim.excerpt}`;
  for (const { pattern, keys: sectionKeys } of FIELD_TO_SED_KEYS) {
    if (pattern.test(haystack)) sectionKeys.forEach((k) => keys.add(k));
  }
  for (const idx of claim.refIndices) {
    const ref = references[idx - 1];
    const refHay = `${ref?.field ?? ''} ${ref?.source ?? ''}`;
    for (const { pattern, keys: sectionKeys } of FIELD_TO_SED_KEYS) {
      if (pattern.test(refHay)) sectionKeys.forEach((k) => keys.add(k));
    }
  }
  if (keys.size === 0) keys.add('summary');
  return [...keys];
}

function compactSedValue(value: unknown, depth = 0): unknown {
  if (value == null || depth > 3) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.slice(0, 8).map((v) => compactSedValue(v, depth + 1));
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj).slice(0, 20)) {
      if (k === 'rawResponse' || k === 'html') continue;
      out[k] = compactSedValue(v, depth + 1);
    }
    return out;
  }
  return String(value);
}

function buildSedExcerpt(
  sed: SiteEvidenceDocument | null,
  claim: QualitativeClaim,
  references: DataSource[],
): string {
  if (!sed) return '(SED unavailable)';
  const sections = pickSedSections(sed, claim, references);
  const parts: string[] = [];
  const sedRecord = sed as unknown as Record<string, unknown>;
  for (const key of sections) {
    if (sedRecord[key] != null) {
      parts.push(`## sed.${key}\n${JSON.stringify(compactSedValue(sedRecord[key]), null, 2)}`);
    }
  }
  if (parts.length === 0) {
    return JSON.stringify(compactSedValue(sed), null, 2).slice(0, 4000);
  }
  return parts.join('\n\n').slice(0, 6000);
}

/** Build cited reference + SED evidence bundle for a claim. */
export function buildClaimEvidence(
  claim: QualitativeClaim,
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null,
): QualitativeClaimEvidence {
  const references = report.references ?? [];
  const citedReferences = claim.refIndices.map((index) => {
    const ref = references[index - 1];
    const excerpt = [
      ref?.source,
      ref?.field ? `field=${ref.field}` : null,
      ref?.retrievedAt ? `retrieved=${ref.retrievedAt}` : null,
    ].filter(Boolean).join(' · ');
    return {
      index,
      source: ref?.source ?? '(unknown source)',
      field: ref?.field,
      excerpt: excerpt || '(no reference metadata)',
    };
  });

  return {
    sed,
    citedReferences,
    sedExcerpt: buildSedExcerpt(sed, claim, references),
  };
}

export function parseClaimJudgement(raw: string | null | undefined): ClaimJudgement | null {
  if (!raw?.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as {
      verdict?: string;
      rationale?: string;
      sedFieldsConsidered?: unknown;
    };
    const verdict = String(parsed.verdict ?? '').toUpperCase();
    if (verdict !== 'CONFIRMED' && verdict !== 'UNSUPPORTED' && verdict !== 'CONTRADICTED') {
      return null;
    }
    const sedFieldsConsidered = Array.isArray(parsed.sedFieldsConsidered)
      ? parsed.sedFieldsConsidered.filter((v): v is string => typeof v === 'string')
      : [];
    return {
      verdict: verdict as ClaimVerdict,
      rationale: typeof parsed.rationale === 'string' ? parsed.rationale.trim() : '',
      sedFieldsConsidered,
    };
  } catch {
    return null;
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return Promise.race([
    promise,
    new Promise<null>((resolve) => { setTimeout(() => resolve(null), ms); }),
  ]);
}

export const defaultQualitativeClaimLlmFn: QualitativeClaimLlmFn = async (prompt, ctx) => {
  try {
    const response = await withTimeout(
      tracedGeminiCall(
        QUALITATIVE_CLAIM_JUDGE_MODEL,
        () => ai.models.generateContent({
          model: QUALITATIVE_CLAIM_JUDGE_MODEL,
          contents: prompt,
          config: {
            responseMimeType: 'application/json',
            responseSchema: {
              type: Type.OBJECT,
              properties: {
                verdict: {
                  type: Type.STRING,
                  description: 'CONFIRMED, UNSUPPORTED, or CONTRADICTED',
                },
                rationale: { type: Type.STRING },
                sedFieldsConsidered: {
                  type: Type.ARRAY,
                  items: { type: Type.STRING },
                },
              },
              required: ['verdict', 'rationale'],
            },
          },
        }),
        {
          siteId: ctx.siteId,
          reportId: ctx.reportId,
          reportType: ctx.reportType,
          stage: 'qualitative-claim-judge',
        },
      ),
      JUDGE_TIMEOUT_MS,
    );
    if (!response) {
      console.warn('[qualitativeClaimAuditor] judge timed out or returned empty');
      return null;
    }
    return (response as { text?: string }).text ?? null;
  } catch (err) {
    console.warn('[qualitativeClaimAuditor] judge failed:', (err as Error)?.message);
    return null;
  }
};

/** Judge a single qualitative claim against evidence via Flash JSON prompt. */
export async function judgeClaim(
  claim: QualitativeClaim,
  evidence: QualitativeClaimEvidence,
  llm: QualitativeClaimLlmFn = defaultQualitativeClaimLlmFn,
  ctx: { siteId?: string; reportId?: string; reportType?: string } = {},
): Promise<ClaimJudgement | null> {
  const prompt = buildQualitativeClaimJudgePrompt({
    claimText: claim.excerpt,
    sectionId: claim.sectionId,
    citedReferences: evidence.citedReferences,
    sedExcerpt: evidence.sedExcerpt,
  });
  const raw = await llm(prompt, ctx).catch((err: Error) => {
    console.warn('[qualitativeClaimAuditor] llm call failed:', err.message);
    return null;
  });
  return parseClaimJudgement(raw);
}

export function verdictToSeverity(verdict: ClaimVerdict): AuditSeverity | null {
  if (verdict === 'CONTRADICTED') return 'block';
  if (verdict === 'UNSUPPORTED') return 'warn';
  return null;
}

export function findingFromJudgement(
  claim: QualitativeClaim,
  judgement: ClaimJudgement,
): QualitativeClaimFinding | null {
  const severity = verdictToSeverity(judgement.verdict);
  if (!severity) return null;
  return {
    claimId: claim.id,
    claimText: claim.text,
    sectionId: claim.sectionId,
    lineNumber: claim.lineNumber,
    refIndices: claim.refIndices,
    verdict: judgement.verdict as 'UNSUPPORTED' | 'CONTRADICTED',
    severity,
    rationale: judgement.rationale,
    sedFieldsConsidered: judgement.sedFieldsConsidered,
    message: `Qualitative claim ${judgement.verdict.toLowerCase()}: "${claim.text.slice(0, 120)}" — ${judgement.rationale}`,
  };
}

export interface AuditQualitativeClaimsOptions {
  maxClaims?: number;
  llm?: QualitativeClaimLlmFn;
  siteId?: string;
  reportId?: string;
  reportType?: string;
  onSkip?: (reason: string, count: number) => void;
}

/** Audit all qualitative claims in a report; returns findings for unsupported/contradicted only. */
export async function auditQualitativeClaims(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null,
  opts: AuditQualitativeClaimsOptions = {},
): Promise<QualitativeClaimAuditResult> {
  const maxClaims = opts.maxClaims ?? DEFAULT_MAX_CLAIMS;
  const llm = opts.llm ?? defaultQualitativeClaimLlmFn;
  const ctx = {
    siteId: opts.siteId ?? report.siteId,
    reportId: opts.reportId ?? report.id,
    reportType: opts.reportType ?? report.reportType,
  };

  const allClaims = extractQualitativeClaims(report);
  const claims = allClaims.slice(0, maxClaims);
  const skipped = Math.max(0, allClaims.length - claims.length);
  if (skipped > 0) {
    const msg = `[qualitativeClaimAuditor] maxClaims=${maxClaims} — skipped ${skipped} claim(s)`;
    console.warn(msg);
    opts.onSkip?.(msg, skipped);
  }

  const findings: QualitativeClaimFinding[] = [];
  let confirmedCount = 0;
  let claimsJudged = 0;

  for (const claim of claims) {
    const evidence = buildClaimEvidence(claim, report, sed);
    const judgement = await judgeClaim(claim, evidence, llm, ctx);
    if (!judgement) continue;
    claimsJudged += 1;
    if (judgement.verdict === 'CONFIRMED') {
      confirmedCount += 1;
      continue;
    }
    const finding = findingFromJudgement(claim, judgement);
    if (finding) findings.push(finding);
  }

  const result: QualitativeClaimAuditResult = {
    findings,
    claimsScanned: allClaims.length,
    claimsJudged,
    claimsSkipped: skipped,
    confirmedCount,
    checkedAt: new Date().toISOString(),
    promptVersion: QUALITATIVE_CLAIM_JUDGE_PROMPT_VERSION,
  };

  logAuditEvent({
    type: 'qualitative-claim-audit-completed',
    siteId: ctx.siteId,
    reportId: ctx.reportId,
    reportType: ctx.reportType,
    payload: {
      claimsScanned: result.claimsScanned,
      claimsJudged: result.claimsJudged,
      claimsSkipped: result.claimsSkipped,
      confirmedCount: result.confirmedCount,
      findingCount: findings.length,
      blockCount: findings.filter((f) => f.severity === 'block').length,
      warnCount: findings.filter((f) => f.severity === 'warn').length,
      promptVersion: result.promptVersion,
    },
  });

  return result;
}

export function renderQualitativeClaimMarkdown(
  siteName: string,
  report: SiteRagReport,
  result: QualitativeClaimAuditResult,
): string {
  const lines = [
    `## ${siteName} — ${report.reportType ?? report.id}`,
    '',
    `- Claims scanned: ${result.claimsScanned}`,
    `- Claims judged: ${result.claimsJudged}`,
    `- Skipped (cap): ${result.claimsSkipped}`,
    `- Confirmed: ${result.confirmedCount}`,
    `- Findings: ${result.findings.length}`,
    '',
  ];
  if (result.findings.length === 0) {
    lines.push('_No unsupported or contradicted qualitative claims._', '');
    return lines.join('\n');
  }
  for (const f of result.findings) {
    lines.push(
      `### ${f.severity.toUpperCase()} — ${f.verdict}`,
      '',
      `- Section: \`${f.sectionId}\` (line ${f.lineNumber})`,
      `- Claim: "${f.claimText}"`,
      `- Refs: ${f.refIndices.map((i) => `[Ref ${i}]`).join(', ') || '—'}`,
      `- Rationale: ${f.rationale}`,
      f.sedFieldsConsidered.length > 0
        ? `- SED fields: ${f.sedFieldsConsidered.join(', ')}`
        : '',
      '',
    );
  }
  return lines.join('\n');
}
